"use client";

import React, { useState, useEffect, useRef } from 'react';
import Counter from "@/components/shared/counter";

interface Stat {
    value: string;
    label: string;
}

interface CollegeStatsComponentProps {
    stats: Stat[];
    size: 'top' | 'bottom'; 
}

const AnimatedNumber = ({ value, className, shouldAnimate }) => {
    const [displayValue, setDisplayValue] = useState(0);
    const finalValue = parseFloat(value.replace(/[^0-9.]/g, ''));
    
    useEffect(() => {
        if (!shouldAnimate) return;
        
        const duration = 1500; // 2 seconds animation
        const steps = 60;
        const stepDuration = duration / steps;
        let current = 0;
        
        const timer = setInterval(() => {
            current += 1;
            const progress = current / steps;
            setDisplayValue(Math.min(progress * finalValue, finalValue));
            
            if (current >= steps) {
                clearInterval(timer);
            }
        }, stepDuration);
        
        return () => clearInterval(timer);
    }, [finalValue, shouldAnimate]);

    if (value.includes('x')) {
        return <span className={className}>{displayValue.toFixed(1)}x</span>;
    }
    if (value.includes('.')) {
        if (value.endsWith('/')) {
            return <span className={className}>{displayValue.toFixed(1)}/5</span>;
        }
        return <span className={className}>{displayValue.toFixed(1)}</span>;
    }
    if (value.endsWith('%')) {
        if (value.startsWith('+')) {
            return <span className={className}>+{Math.round(displayValue)}%</span>;
        }
        return <span className={className}>{Math.round(displayValue)}%</span>;
    }
    return <span className={className}>{Math.round(displayValue)}</span>;
};

const CollegeStatsComponent: React.FC<CollegeStatsComponentProps> = ({ stats, size }) => {
    const [isVisible, setIsVisible] = useState(false);
    const statsRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            {
                threshold: 0.1
            }
        );

        if (statsRef.current) {
            observer.observe(statsRef.current);
        }

        return () => observer.disconnect();
    }, []);

    return (
        <div 
            ref={statsRef}
            className={`flex flex-col gap-5 items-center lg:items-stretch lg:flex-row justify-around mt-8 md:mt-16`}
        >
            {stats.map((stat, index) => (
                <div key={index} className="text-center mb-4 md:mb-0 w-[250px]">
                    <p className={`font-semibold ${size === 'bottom' ? 'text-3xl lg:text-5xl' : 'text-5xl md:text-6xl'}`}>
                        <AnimatedNumber 
                            value={stat.value} 
                            className={`font-semibold ${size === 'bottom' ? 'text-3xl lg:text-5xl' : 'text-5xl md:text-6xl'}`}
                            shouldAnimate={isVisible}
                        />
                    </p>
                    <p className="text-sm font-semibold text-gray-500 mt-2" style={{ whiteSpace: 'pre-line' }}>
                        {stat.label}
                    </p>
                </div>
            ))}
        </div>
    );
};

export default CollegeStatsComponent;